/**
 * Supabase client — platform-aware session storage.
 *
 * Web: cookie-based sessions via @supabase/ssr (first-party cookies persist
 * across page reloads automatically).
 *
 * Native (Capacitor): standard createClient with localStorage. Cookies don't
 * work reliably in WKWebView/Android WebView when the origin is
 * capacitor://localhost, so we fall back to the localStorage-based auth.
 *
 * @see https://supabase.com/docs/guides/auth/server-side/creating-a-client
 */
import { createBrowserClient } from "@supabase/ssr";
import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import type { AppDatabase } from "./database-extensions";
import { logger } from "@/lib/logger";
import { getActiveSupportMode } from "./support-mode-ref";
import { createSavedBatchRpcTransport, SAVED_BATCH_ADAPTER_CONTRACT_VERSION, type SavedBatchAuthenticatedContext } from "./saved-batch-rpc";

const _proc =
  typeof process !== "undefined"
    ? process.env
    : ({} as Record<string, string | undefined>);
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || _proc.VITE_SUPABASE_URL || "";
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  _proc.VITE_SUPABASE_PUBLISHABLE_KEY ||
  "";
const SUPABASE_SCHEMA = import.meta.env.VITE_SUPABASE_SCHEMA || _proc.VITE_SUPABASE_SCHEMA || "public";

const isBrowser = typeof window !== "undefined";
const isNative = Capacitor.isNativePlatform();

// ─── Startup diagnostic (visible in Xcode / Android Studio console) ──────────
if (isBrowser) {
  console.log("[Forge:Boot] Supabase URL =", SUPABASE_URL);
  console.log("[Forge:Boot] Platform =", Capacitor.getPlatform(), "| isNative =", isNative);
  console.log("[Forge:Boot] Auth strategy =", isNative ? "localStorage (createClient)" : "cookies (createBrowserClient)");
}

// ---------------------------------------------------------------------------
// Legacy cleanup — one-time removal of old localStorage auth artifacts.
// These keys were used by the previous auth-refresh / localStorage fallback
// architecture. Clearing them prevents stale tokens from interfering.
// ---------------------------------------------------------------------------
const LEGACY_AUTH_KEYS = [
  "supabase.auth.token",
  "sb-auth-token",
  "forge_refresh_token_fallback",
];

const cleanupLegacyAuthStorage = () => {
  if (!isBrowser || isNative) return;
  try {
    for (const key of LEGACY_AUTH_KEYS) {
      window.localStorage.removeItem(key);
    }
    // Also remove any sb-*-auth-token keys from older Supabase versions
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage may be blocked — ignore
  }
};

// ---------------------------------------------------------------------------
// Support-mode fetch wrapper
//
// Three jobs:
//
//   (1) Inject `x-support-session-id` on every request while support mode
//       is active. The DB function `get_support_session_org_id()` reads
//       this header via `current_setting('request.headers')` to scope
//       tenant access on the server. Without the header, the entire
//       RLS-side support-mode mechanism is inert — admins reach tenant
//       data via their direct `platform_super_admin` role and the
//       "support session" is only a client-side UI marker.
//
//   (2) Emit a `support_session_audit` row after every successful
//       mutation (POST/PATCH/PUT/DELETE) while support mode is active.
//       The `AdminSupportSessions` "Actions Performed" timeline reads
//       this table — until this wrapper landed, the column was
//       permanently `0` because nothing in the codebase wrote to it.
//
//   (3) Stay invisible when support mode is NOT active. No header
//       injection, no audit, no side effect — vanilla fetch passthrough.
//
// We deliberately do NOT block mutations. Support mode exists so platform
// admins can FIX tenant data ("I see you have a broken pledge — let me
// repair the FK"). Read-only support mode is a different feature; the
// "Audited" badge in AdminLayout reflects what this wrapper actually
// promises.
//
// Recursion guard: the audit-write itself goes through this same fetch
// wrapper. We detect writes to the audit table by URL and skip the
// side-effect there so we don't loop.
// ---------------------------------------------------------------------------

const SUPABASE_REST_PATH = "/rest/v1/";
const AUDIT_TABLE_NAME = "support_session_audit";

function parseSupabaseRequest(url: string): {
  kind: "table" | "rpc" | "other";
  name: string | null;
} {
  const idx = url.indexOf(SUPABASE_REST_PATH);
  if (idx < 0) return { kind: "other", name: null };
  const afterRest = url.slice(idx + SUPABASE_REST_PATH.length).split("?")[0];
  if (afterRest.startsWith("rpc/")) {
    return { kind: "rpc", name: afterRest.slice(4) };
  }
  const tableName = afterRest.split("/")[0];
  if (!tableName) return { kind: "other", name: null };
  return { kind: "table", name: tableName };
}

function isMutatingMethod(method: string): boolean {
  return method === "POST" || method === "PATCH" || method === "PUT" || method === "DELETE";
}

/**
 * Fire-and-forget audit write. Uses raw `fetch` (not the supabase client) so
 * we skip the wrapper entirely and avoid the recursion check + double-await
 * of the client's PostgREST builder.
 */
async function emitSupportSessionAudit(args: {
  sessionId: string;
  adminUserId: string;
  targetOrgId: string;
  method: string;
  kind: "table" | "rpc";
  name: string;
}): Promise<void> {
  try {
    const session = (await supabase.auth.getSession()).data.session;
    if (!session?.access_token) return;
    const action =
      args.kind === "rpc" ? `rpc:${args.name}` : `${args.method.toLowerCase()}:${args.name}`;
    await fetch(`${SUPABASE_URL}${SUPABASE_REST_PATH}${AUDIT_TABLE_NAME}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${session.access_token}`,
        Prefer: "return=minimal",
        // Don't inject x-support-session-id here — RLS for the audit
        // table reads admin_user_id from auth.uid() server-side; the
        // session id is in the payload below.
      },
      body: JSON.stringify({
        support_session_id: args.sessionId,
        admin_user_id: args.adminUserId,
        target_org_id: args.targetOrgId,
        action,
        table_name: args.kind === "table" ? args.name : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      }),
    });
  } catch (error) {
    // Audit-write failures must never block the user-visible request.
    // Surface in DevTools so we still notice the breakage.
    console.warn("[support-mode] audit-write failed", error);
  }
}

function createSupportModeFetch(): typeof fetch {
  return async (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? "GET").toUpperCase();
    const supportMode = getActiveSupportMode();

    let nextInit = init;
    if (supportMode) {
      const headers = new Headers(init?.headers);
      // Idempotent: setting the same header twice is fine.
      headers.set("x-support-session-id", supportMode.sessionId);
      nextInit = { ...(init ?? {}), headers };
    }

    const response = await fetch(input, nextInit);

    // Audit only successful mutations on REST endpoints. Skip writes to
    // the audit table itself (recursion) and skip when there's no active
    // support session. RPC calls are audited because we can't tell from
    // the URL alone whether an RPC mutates — capturing them all is
    // better than missing the mutating ones.
    if (
      supportMode &&
      response.ok &&
      isMutatingMethod(method)
    ) {
      const parsed = parseSupabaseRequest(url);
      if (parsed.kind !== "other" && parsed.name && parsed.name !== AUDIT_TABLE_NAME) {
        void emitSupportSessionAudit({
          sessionId: supportMode.sessionId,
          adminUserId: supportMode.adminUserId,
          targetOrgId: supportMode.targetOrgId,
          method,
          kind: parsed.kind,
          name: parsed.name,
        });
      }
    }

    return response;
  };
}

// ---------------------------------------------------------------------------
// Supabase client — platform-aware
// ---------------------------------------------------------------------------

function createSupabaseClient() {
  const authOptions = {
    flowType: "pkce" as const,
    detectSessionInUrl: isNative, // Enable on native for deep link OAuth
    persistSession: true,
    autoRefreshToken: true,
  };

  const dbOptions = { schema: SUPABASE_SCHEMA };
  const supportModeFetch = createSupportModeFetch();

  if (isNative) {
    // Native: use standard createClient (localStorage-based auth).
    // Cookies from capacitor://localhost don't work cross-origin in WKWebView.
    return createClient<AppDatabase>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      db: dbOptions,
      auth: authOptions,
      global: { fetch: supportModeFetch },
      realtime: {
        // iOS URLSession-based networking can be slower to establish WebSocket
        // connections after app backgrounding or rapid navigation.  Increase
        // the default timeout and keep heartbeat aggressive to stay connected.
        timeout: 30_000, // 30s (default 10s)
        heartbeatIntervalMs: 15_000,
      },
    });
  }

  // Web: use @supabase/ssr cookie-based client.
  return createBrowserClient<AppDatabase>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    db: dbOptions,
    global: { fetch: supportModeFetch },
    auth: {
      flowType: "pkce",
      // Keep disabled so non-Supabase OAuth callback routes can safely read
      // `code` / `state` URL params without Supabase intercepting them.
      detectSessionInUrl: false,
      persistSession: true,
      autoRefreshToken: true,
    },
  });
}

export const supabase = createSupabaseClient();

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

/**
 * Clear session artifacts for the current device only.
 * Other devices / tabs retain their sessions.
 */
export const clearSessionArtifacts = async () => {
  if (!isBrowser) return;
  logger.debug("[Auth] Clearing session artifacts (local)");
  await supabase.auth.signOut({ scope: "local" });
};

/**
 * Clear session artifacts across ALL devices (global sign-out).
 * Only used when the user explicitly requests "log out all devices".
 */
export const clearSessionArtifactsGlobal = async () => {
  if (!isBrowser) return;
  logger.debug("[Auth] Clearing session artifacts (global - all devices)");
  await supabase.auth.signOut({ scope: "global" });
};

/**
 * Proactive session freshness guard.
 *
 * Call before any Supabase write (mutation) to ensure the JWT is still valid.
 * If the token will expire within the grace window, triggers an explicit
 * `refreshSession()` before the query runs.
 */
const PREFLIGHT_GRACE_SECONDS = 120;
let preflightInProgress: Promise<boolean> | null = null;

export const ensureFreshSession = async (): Promise<boolean> => {
  if (preflightInProgress) {
    return preflightInProgress;
  }

  const run = async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const expiresAt = session.expires_at;
      if (!expiresAt) return true;

      const nowSeconds = Math.floor(Date.now() / 1000);
      const remaining = expiresAt - nowSeconds;

      if (remaining > PREFLIGHT_GRACE_SECONDS) {
        return true;
      }

      logger.debug("[Auth] Preflight: token expiring soon, refreshing", {
        remainingSeconds: remaining,
      });

      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) {
        logger.warn("[Auth] Preflight refresh failed", {
          error: logger.sanitizeForUser(error?.message || "no session after refresh"),
        });
        return remaining > 0;
      }

      logger.debug("[Auth] Preflight refresh succeeded");
      return true;
    } catch (error) {
      logger.warn("[Auth] Preflight session check failed", {
        error: logger.sanitizeForUser(error instanceof Error ? error.message : "preflight_error"),
      });
      return true;
    }
  };

  preflightInProgress = run().finally(() => {
    preflightInProgress = null;
  });

  return preflightInProgress;
};

/** Public backend identity for isolating drafts and operation recovery. */
export function getSavedBatchBackendId(): string {
  return JSON.stringify([SUPABASE_URL.replace(/\/$/, ""), SUPABASE_SCHEMA, SAVED_BATCH_ADAPTER_CONTRACT_VERSION]);
}

/** Real authenticated binding for the seven coherent saved-batch RPCs. */
export function createAuthenticatedSavedBatchTransport(
  expected: SavedBatchAuthenticatedContext,
  isCurrent: () => boolean,
) {
  return createSavedBatchRpcTransport(expected, isCurrent, {
    url: SUPABASE_URL,
    publishableKey: SUPABASE_PUBLISHABLE_KEY,
    schema: SUPABASE_SCHEMA,
    ensureFreshSession,
    getSession: () => supabase.auth.getSession(),
    getSupportMode: getActiveSupportMode,
    fetch: createSupportModeFetch(),
  });
}

// Run legacy cleanup on module load
if (isBrowser) {
  cleanupLegacyAuthStorage();
}
